'use client';

import { type ReactNode } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Text } from '../../components/styled/text';
import { MarkdownPreview } from '../markdown-preview';

export type EvidenceCardWithDataProps = {
  explanation?: string;
  data?: string;
  children?: ReactNode;
  actions?: ReactNode;
  detail?: ReactNode;
  escapeExplanationRawHtml?: boolean;
  index?: number;
  evidenceType?: 'email' | 'websocket' | 'oob';
};

export function EvidenceCardWithData({
  explanation,
  data,
  children,
  actions,
  detail,
  escapeExplanationRawHtml,
  index,
  evidenceType,
}: EvidenceCardWithDataProps) {
  const tArtifacts = useTranslations('designSystem.artifacts');
  const tEditor = useTranslations('designSystem.findings.structuredPocEditor');
  const title =
    typeof index === 'number'
      ? tArtifacts('evidenceLabel', { index: index + 1 })
      : tEditor('evidence');
  const typeLabel = evidenceType ? tArtifacts(evidenceType) : undefined;
  const body = children ?? (
    <MarkdownPreview source={data ?? ''} fontSize={12} compact />
  );

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="bg.subtle"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={3}
        h="36px"
        bg="bg.subtle"
        borderBottomWidth="1px"
        borderBottomColor="border.neutral.muted"
      >
        <HStack gap={1.5} alignItems="center" minW={0}>
          <Text fontSize="xs" fontWeight="medium" color="fg.default" truncate>
            {title}
          </Text>
          {typeLabel && (
            <Text fontSize="xs" color="fg.muted" truncate>
              {typeLabel}
            </Text>
          )}
        </HStack>
        <HStack gap={2} alignItems="center" flexShrink={0}>
          {detail}
          {actions && (
            <HStack gap={1} alignItems="center">
              {actions}
            </HStack>
          )}
        </HStack>
      </HStack>
      {explanation && (
        <Box
          bg="bg.subtle"
          px={3}
          py={2}
          color="fg.default"
          borderBottomWidth="1px"
          borderBottomColor="border.neutral.muted"
        >
          <MarkdownPreview
            source={explanation}
            fontSize={12}
            compact
            escapeRawHtml={escapeExplanationRawHtml}
          />
        </Box>
      )}
      <Box px={3} py={2} bg="bg.subtle">
        {body}
      </Box>
    </Box>
  );
}
